import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useFormikContext, getIn } from 'formik';
import Field from 'core/components/form/form.v2';

const IsolatedCoverageD = ({ fieldName, disabled, userEditingRef, onFocus, onBlur }) => {
  const { setFieldValue, values } = useFormikContext();
  const fieldPath = `${fieldName}.coverages.coverageD`;
  const [localValue, setLocalValue] = useState(() => getIn(values, fieldPath) ?? '');
  const debounceTimer = useRef(null);

  // Sync with Formik when field is not being edited
  const formikValue = getIn(values, fieldPath) ?? '';
  const prevFormikValueRef = useRef(formikValue);

  useEffect(() => {
    const currentFormikValue = getIn(values, fieldPath) ?? '';
    if (!userEditingRef.current && prevFormikValueRef.current !== currentFormikValue) {
      setLocalValue(currentFormikValue);
      prevFormikValueRef.current = currentFormikValue;
    }
  }, [values, fieldPath, userEditingRef]);

  const handleFocus = useCallback(() => {
    userEditingRef.current = true;
    if (onFocus) {
      onFocus();
    }
  }, [userEditingRef, onFocus]);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      userEditingRef.current = false;
      if (onBlur) {
        onBlur();
      }

      // Sync final value to Formik
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      setFieldValue(fieldPath, localValue);
    }, 100);
  }, [userEditingRef, onBlur, setFieldValue, fieldPath, localValue]);

  const handleChange = useCallback(
    (value) => {
      setLocalValue(value);

      // Debounced update to Formik
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        setFieldValue(fieldPath, value);
      }, 300);
    },
    [setFieldValue, fieldPath]
  );

  return (
    <Field
      key={fieldPath}
      id={fieldPath}
      name={fieldPath}
      type="numeric"
      mode="dark"
      format={{ thousandSeparator: true, prefix: '$' }}
      disabled={disabled}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

// Heavy memoization to prevent ANY re-renders unless absolutely necessary
export default React.memo(IsolatedCoverageD, (prevProps, nextProps) => {
  return (
    prevProps.fieldName === nextProps.fieldName &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.userEditingRef === nextProps.userEditingRef &&
    prevProps.onFocus === nextProps.onFocus &&
    prevProps.onBlur === nextProps.onBlur
  );
});
